import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import {
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { QueryErrorAlert } from "src/grading/components/QueryStateView/QueryStateView";
import { DistrictStudentMatch } from "src/models/hierarchy";
import { useSearchDistrictStudentsQuery } from "src/store/reducers/districts/privacyApiSlice";

export interface StudentSearchFieldProps {
  districtId: string;
  value: DistrictStudentMatch | null;
  onChange: (value: DistrictStudentMatch | null) => void;
  /** Field label; "Student" by default. */
  label?: string;
  helperText?: ReactNode;
  disabled?: boolean;
  /** Stable id for the e2e suite, on the text input. */
  "data-testid"?: string;
  /** Debounce in ms before the term is searched; 250 by default. */
  delay?: number;
}

const useDebouncedValue = <V,>(value: V, delay: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const withoutKey = <P extends { key?: unknown }>({
  key: _key,
  ...rest
}: P): Omit<P, "key"> => rest;

/**
 * Finds one of the district's students, active or inactive, by name or SIS id (FR-017). Each match
 * shows the name, school, grade and SIS id, so two students with the same name can be told apart.
 */
export const StudentSearchField = ({
  districtId,
  value,
  onChange,
  label,
  helperText,
  disabled,
  "data-testid": testId,
  delay = 250,
}: StudentSearchFieldProps) => {
  const { t } = useTranslation();
  // The typed text is ours, not the Autocomplete's: leaving the field (for example to press Retry
  // on a search that couldn't run) keeps it, so Retry searches the same text again.
  const [inputValue, setInputValue] = useState("");
  // A picked student cleared from outside (the form reset) takes its name out of the field.
  const picked = useRef(value);
  useEffect(() => {
    const previous = picked.current;
    picked.current = value;
    if (previous && !value)
      setInputValue((current) =>
        current === previous.displayName ? "" : current,
      );
  }, [value]);
  const typed = inputValue.trim();
  // A picked student's name sits in the input; it is not a new search.
  const searchable = value && inputValue === value.displayName ? "" : typed;
  const term = useDebouncedValue(searchable, delay);
  const search = useSearchDistrictStudentsQuery(
    { districtId, q: term },
    { skip: !term },
  );
  const { data, isLoading, isFetching } = search;
  const searching =
    Boolean(term) && (Boolean(isLoading || isFetching) || term !== searchable);
  // FR-048: a search that couldn't run says so; it never reads as "No matches".
  const searchFailed = Boolean(term) && !searching && Boolean(search.isError);

  const details = (option: DistrictStudentMatch) =>
    [
      option.schoolName,
      option.grade !== null && option.grade !== undefined && option.grade !== ""
        ? t("dataRequests.search.grade", {
            defaultValue: "Grade {{grade}}",
            grade: option.grade,
          })
        : null,
      option.externalId
        ? t("dataRequests.search.sisId", {
            defaultValue: "SIS id {{id}}",
            id: option.externalId,
          })
        : null,
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <>
      <Autocomplete<DistrictStudentMatch, false, false, false>
        disabled={disabled}
        options={term ? (data ?? []) : value ? [value] : []}
        value={value}
        onChange={(_event, next) => onChange(next)}
        inputValue={inputValue}
        onInputChange={(_event, next) => setInputValue(next)}
        // With no student picked, blurring keeps the search text; with one picked, it restores
        // the picked name over a half-typed edit.
        clearOnBlur={Boolean(value)}
        // The server already matched the term against name and SIS id.
        filterOptions={(options) => options}
        getOptionLabel={(option) => option.displayName}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        loading={searching}
        loadingText={t("common.loading")}
        noOptionsText={
          searchFailed
            ? t("dataRequests.search.error")
            : typed
              ? t("picker.noMatches")
              : t("dataRequests.search.typeToSearch", {
                  defaultValue: "Type a name or SIS id",
                })
        }
        renderOption={(props, option) => (
          <Box component="li" {...withoutKey(props)} key={option.id}>
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <Box component="span" sx={{ fontWeight: 700 }}>
                {option.displayName}
              </Box>
              <Box
                component="span"
                sx={{ fontSize: 13, color: "text.secondary" }}
              >
                {details(option)}
              </Box>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              label ??
              t("dataRequests.search.label", { defaultValue: "Student" })
            }
            helperText={
              helperText ??
              t("dataRequests.search.helper", {
                defaultValue: "Search by name or SIS id",
              })
            }
            inputProps={
              {
                ...params.inputProps,
                ...(testId ? { "data-testid": testId } : {}),
              } as InputHTMLAttributes<HTMLInputElement>
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searching ? (
                    <CircularProgress
                      color="inherit"
                      size={18}
                      aria-hidden="true"
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {searchFailed ? (
        <QueryErrorAlert query={search} data-testid="student-search-error">
          {t("dataRequests.search.error")}
        </QueryErrorAlert>
      ) : null}
    </>
  );
};

export default StudentSearchField;
